/** Admin → Tenpoint: the platform administrators. The surface that replaced
 *  the bootstrap script; underneath stays platform_admins, never a companies
 *  row, so admin authority is never a column someone can set. */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../api';
import { adminApi, fmtDate, timeAgo, type StaffMember } from '../../adminApi';
import { AdminTopCrumb, useAdmin } from '../../adminShell';
import { InvitePanel } from '../../components/InvitePanel';

export function Staff() {
  const admin = useAdmin();
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [invite, setInvite] = useState<{ email: string; link: string; account?: string } | null>(null);
  const [failed, setFailed] = useState(false);

  function load() {
    return adminApi
      .staff()
      .then((d) => {
        setStaff(d.staff);
        setSelfId(d.self);
      })
      .catch(() => setFailed(true));
  }
  useEffect(() => {
    void load();
  }, []);

  function setStatus(id: string, status: 'active' | 'disabled') {
    if (status === 'disabled' && !confirm('Remove this person’s access to every customer account?')) return;
    adminApi
      .setStaffStatus(id, status)
      .then(load)
      .catch((err) => {
        alert(
          err instanceof ApiError && err.code === 'last_active_admin'
            ? 'That is the last active administrator. Add another before disabling this one.'
            : err instanceof ApiError && err.code === 'cannot_disable_self'
              ? 'You cannot disable your own access. Ask another administrator.'
              : 'Could not change that administrator’s status.',
        );
      });
  }

  const list = staff ?? [];

  return (
    <>
      <AdminTopCrumb>
        <b>Tenpoint</b>
      </AdminTopCrumb>
      <main className="page" id="main">
        <div className="page__head">
          <div>
            <h1>Tenpoint</h1>
            <p className="sub">The people who administer the software</p>
          </div>
          <div className="spacer" />
          <button className="btn btn--primary" onClick={() => setAdding(true)}>
            Add staff
          </button>
        </div>

        {invite && <InvitePanel email={invite.email} link={invite.link} account={invite.account} onDone={() => setInvite(null)} />}

        {adding && (
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel__head">
              <h2>Add a Tenpoint administrator</h2>
              <div className="spacer" />
              <button className="btn btn--sm" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
            <div className="panel__body">
              <p className="note" style={{ marginBottom: 16 }}>
                Platform administrators can see and act inside <b>every</b> customer account. Their actions are written to the
                customer’s own audit log.
              </p>
              <AddStaffForm
                onDone={(inv) => {
                  setAdding(false);
                  if (inv) setInvite(inv);
                  void load();
                }}
              />
            </div>
          </div>
        )}

        <div className="panel">
          <div className="panel__head">
            <h2>Administrators</h2>
            <div className="spacer" />
            <span className="tag tag--grey">{staff ? `${list.filter((s) => s.status === 'active').length} active` : '–'}</span>
          </div>
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Added</th>
                  <th>Last active</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {failed && (
                  <tr>
                    <td colSpan={6} className="dim" style={{ padding: '22px 13px' }}>
                      Could not load administrators.
                    </td>
                  </tr>
                )}
                {list.map((s) => {
                  const isSelf = s.id === selfId || s.id === admin.id;
                  return (
                    <tr key={s.id}>
                      <td className="strong">{s.name}</td>
                      <td className="dim">{s.email}</td>
                      <td className="dim">{fmtDate(s.createdAt)}</td>
                      <td className="dim">{timeAgo(s.lastActiveAt)}</td>
                      <td>
                        <span className={`tag tag--${s.status === 'active' ? 'ok' : 'grey'}`}>
                          {s.status === 'active' ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isSelf ? (
                          <span className="dim">You</span>
                        ) : s.status === 'active' ? (
                          <button className="btn btn--sm btn--danger" onClick={() => setStatus(s.id, 'disabled')}>
                            Disable
                          </button>
                        ) : (
                          <button className="btn btn--sm" onClick={() => setStatus(s.id, 'active')}>
                            Re-enable
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="panel__foot">
            Tenpoint staff are a separate identity from customer users — they have no organization and are not subject to
            tenant row level security.
          </div>
        </div>
      </main>
    </>
  );
}

function AddStaffForm({ onDone }: { onDone: (invite: { email: string; link: string; account?: string } | null) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    adminApi
      .createStaff({ name: name.trim(), email: email.trim() })
      .then((result) =>
        onDone(result.setPasswordLink ? { email: email.trim(), link: result.setPasswordLink, account: result.idpAccount } : null),
      )
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'admin_exists'
            ? 'That email is already a Tenpoint administrator.'
            : 'Could not add that administrator.',
        );
        setBusy(false);
      });
  }

  return (
    <form className="form" onSubmit={onSubmit}>
      <div className="field--row">
        <div className="field">
          <label htmlFor="s-name">Full name</label>
          <input id="s-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="s-email">Email</label>
          <input id="s-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>
      {error && <p className="login__error">{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn--primary" type="submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add administrator'}
        </button>
      </div>
    </form>
  );
}
